import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Activity,
  KeyRound,
  Archive,
  ClockAlert,
  HardDrive,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Mailbox,
  Megaphone,
  MonitorSmartphone,
  ScrollText,
  Settings2,
  ShieldCheck,
  ShieldOff,
  Stethoscope,
  ShieldAlert,
  Tags,
  ShoppingBag,
  Type,
  TreePine,
  Users,
  Wrench,
  X,
  BarChart3,
  Lightbulb,
  MessagesSquare,
} from 'lucide-react';
import apiClient from '@/api/client';
import { useAdminAuth } from '@/store/useAdminAuth';

// ─── Rahmen der Betreiberkonsole ─────────────────────────────────────────────
//
// Dunkle Leiste und Seitenspalte: wer hier arbeitet, soll auf einen Blick
// sehen, dass er NICHT in der Anwendung ist. Der Rest übernimmt das
// Erscheinungsbild der App – gleiche Schrift, gleiche Karten, gleiche Farben
// für Aktionen.

interface NavGruppe {
  titel: string;
  eintraege: { label: string; pfad: string; icon: typeof Wrench; bereit?: boolean }[];
}

const NAV: NavGruppe[] = [
  {
    titel: 'Überblick',
    eintraege: [
      { label: 'Übersicht', pfad: '/', icon: LayoutDashboard, bereit: true },
      { label: 'Systemzustand', pfad: '/system', icon: Stethoscope, bereit: true },
      { label: 'Sicherheit & Nutzung', pfad: '/sicherheit', icon: ShieldAlert, bereit: true },
      { label: 'Mein Zugang', pfad: '/zugang', icon: KeyRound, bereit: true },
    ],
  },
  {
    titel: 'Betrieb',
    eintraege: [
      { label: 'Einstellungen', pfad: '/einstellungen', icon: Settings2, bereit: true },
      { label: 'Tarife', pfad: '/tarife', icon: Tags, bereit: true },
      { label: 'Shop', pfad: '/shop', icon: ShoppingBag, bereit: true },
      { label: 'Texte & Titel', pfad: '/texte', icon: Type, bereit: true },
      { label: 'Wartungsmodus', pfad: '/wartung', icon: ClockAlert, bereit: true },
      { label: 'Speicher', pfad: '/speicher', icon: HardDrive, bereit: true },
      { label: 'Sitzungen', pfad: '/sitzungen', icon: MonitorSmartphone, bereit: true },
      { label: 'Benachrichtigungen', pfad: '/benachrichtigungen', icon: Activity, bereit: true },
      { label: 'E-Mail', pfad: '/email', icon: Mail, bereit: true },
      { label: 'Rundnachrichten', pfad: '/rundnachrichten', icon: Megaphone, bereit: true },
      { label: 'Versandprotokoll', pfad: '/versandprotokoll', icon: Mailbox, bereit: true },
      { label: 'Abstimmungen', pfad: '/mitwirkung', icon: BarChart3, bereit: true },
      { label: 'Vorschläge', pfad: '/vorschlaege', icon: Lightbulb, bereit: true },
      { label: 'Forum', pfad: '/forum', icon: MessagesSquare, bereit: true },
    ],
  },
  {
    titel: 'Daten',
    eintraege: [
      { label: 'Sicherungen', pfad: '/sicherungen', icon: Archive, bereit: true },
      { label: 'Stammbäume', pfad: '/stammbaeume', icon: TreePine, bereit: true },
    ],
  },
  {
    titel: 'Zugang',
    eintraege: [
      { label: 'Konten', pfad: '/konten', icon: Users, bereit: true },
      { label: 'Rollenvorlagen', pfad: '/rollen', icon: ShieldCheck, bereit: true },
      { label: 'Sperren', pfad: '/sperren', icon: ShieldOff, bereit: true },
      { label: 'Protokoll', pfad: '/protokoll', icon: ScrollText, bereit: true },
    ],
  },
];

export function AdminLayout() {
  const navigate = useNavigate();
  const user = useAdminAuth((s) => s.user);
  const abmelden = useAdminAuth((s) => s.abmelden);
  const [menueOffen, setMenueOffen] = useState(false);

  const ausloggen = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Auch wenn der Server nicht antwortet: lokal abmelden.
    }
    abmelden();
    navigate('/');
  };

  return (
    // Feste Bildschirmhöhe statt min-h-screen: die übernommenen Seiten
    // bringen eigene Scrollbereiche mit (Tabellen, Protokoll), die eine
    // begrenzte Höhe brauchen. Gescrollt wird deshalb IM Inhaltsbereich,
    // nicht im Dokument – sonst gäbe es zwei Bildlaufleisten übereinander.
    <div className="flex h-screen flex-col overflow-hidden bg-slate-100">
      {/* Kopfleiste */}
      <header className="z-30 flex h-14 shrink-0 items-center justify-between gap-3 bg-slate-900 px-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMenueOffen((o) => !o)}
            className="rounded-lg p-1.5 text-slate-300 hover:bg-slate-800 lg:hidden"
            aria-label={menueOffen ? 'Menü schließen' : 'Menü öffnen'}
          >
            {menueOffen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-slate-400" aria-hidden="true" />
            <span className="font-semibold">Betrieb</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-slate-400 sm:inline">{user?.email}</span>
          <button
            onClick={ausloggen}
            className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            Abmelden
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Seitenspalte */}
        <nav
          className={`${
            menueOffen ? 'block' : 'hidden'
          } w-full shrink-0 overflow-y-auto border-b border-slate-200 bg-white p-3 lg:block lg:w-60 lg:border-b-0 lg:border-r`}
        >
          {NAV.map((gruppe) => (
            <div key={gruppe.titel} className="mb-4 last:mb-0">
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                {gruppe.titel}
              </p>
              <ul className="space-y-0.5">
                {gruppe.eintraege.map((e) => (
                  <li key={e.pfad}>
                    <NavLink
                      to={e.pfad}
                      onClick={() => setMenueOffen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm ${
                          isActive
                            ? 'bg-blue-50 font-medium text-blue-700'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`
                      }
                    >
                      <e.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">{e.label}</span>
                      {!e.bereit && (
                        // Ehrlich kennzeichnen, was noch nicht umgezogen ist –
                        // besser als ein Eintrag, der ins Leere führt.
                        <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                          folgt
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <main className="min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
